import { IAppBar } from '@nocode/types';
import React, { FC } from 'react';
import { Dimensions, Platform, View, StatusBar } from 'react-native';
import { Appbar } from 'react-native-paper';
import validateColor from 'validate-color';
import PointIcon from '../CustomIcon';
import { checkFont } from '../func';
import { getActions } from '../../shared';
import { processStyles } from '../util.style';
import AppBarConfig from './config';

const iconAppBarWidth = 48;

const getColor = (color: string) => {
  return validateColor(color) ? color : '#4259AC';
};

const AppBar: FC<IAppBar> = (props) => {
  const {
    y,
    attributes,
    onPress,
    actions,
    style,
    libraryComponentManifest,
    ...otherProps
  } = props;

  // Process styles with simple utility
  const { finalStyle, componentProps } = processStyles({
    attributes,
    style,
    libraryComponentManifest,
    ...otherProps,
  });

  // Extract component-specific props (non-style attributes)
  const {
    leftIcon,
    title,
    rightIcon1,
    rightIcon2,
    originX = 1,
  } = componentProps;

  const backgroundColor = getColor(finalStyle.backgroundColor || '#4259AC');
  const color = getColor(finalStyle.color || '#ffffff');
  const borderColor = getColor(finalStyle.borderColor || '#000000');

  const { width } = Dimensions.get('window');

  const handlePress = (id: string) => {
    const arrayAction = getActions(props, id);
    onPress && onPress(arrayAction, {});
  };

  const normalizedOriginX = originX < 1 ? originX : 1;
  const checkState = () => {
    return (
      <Appbar.Action
        style={{ display: 'flex', alignItems: 'center' }}
        color={color}
        icon={''}
        animated={false}
      />
    );
  };

  const headerStyle = {
    paddingTop: Platform.OS === 'web' && y === 0 ? 20 * normalizedOriginX :
                Platform.OS === 'android' && y === 0 ? StatusBar.currentHeight : 0,
    height: props.height,
    backgroundColor: backgroundColor,
    opacity: finalStyle.opacity || 1,
    zIndex: finalStyle.zIndex || 1,
    ...Platform.select({
      web: {
        borderStyle:
          (finalStyle.borderStyle === 'none'
            ? 'solid'
            : finalStyle.borderStyle) || 'solid',
        borderBottomColor: borderColor,
        borderBottomWidth: finalStyle.borderWidth || 0,
      },
      ios: {
        borderRadius: 1,
      },
      android: {
        borderRadius: 1,
      },
    }),
  };

  // Create title style from processed text styles
  const titleStyle = {
    fontSize: +(finalStyle.fontSize || 16) * normalizedOriginX,
    fontFamily: checkFont(finalStyle.fontFamily || 'System'),
    fontWeight: finalStyle.fontWeight || 'normal',
    color: color,
    alignSelf: 'center' as const,
  };

  return (
    <View
      style={{
        opacity: finalStyle.opacity || 1,
      }}
    >
      <Appbar.Header style={headerStyle}>
        {leftIcon && leftIcon?.enabled ? (
          <Appbar.Action
            color={color}
            icon={
              leftIcon?.icon === 'point'
                ? () => <PointIcon width={24} height={24} fill={color} />
                : leftIcon?.icon
            }
            onPress={() => {
              handlePress('leftIcon');
            }}
            animated={false}
          />
        ) : (
          <View style={{ width: iconAppBarWidth }} />
        )}

        {rightIcon1?.enabled && checkState()}

        <Appbar.Content
          titleStyle={titleStyle}
          title={title?.text}
          color={color}
        />

        {rightIcon1?.enabled && !rightIcon2?.enabled ? checkState() : null}

        {rightIcon1 && rightIcon1.enabled && (
          <Appbar.Action
            color={color}
            icon={
              rightIcon1.icon === 'point'
                ? () => <PointIcon width={24} height={24} fill={color} />
                : rightIcon1.icon
            }
            onPress={() => {
              handlePress('rightIcon1');
            }}
            animated={false}
          />
        )}

        {rightIcon2 && rightIcon2.enabled && (
          <Appbar.Action
            color={color}
            icon={
              rightIcon2.icon === 'point'
                ? () => <PointIcon width={24} height={24} fill={color} />
                : rightIcon2.icon
            }
            onPress={() => {
              handlePress('rightIcon2');
            }}
            animated={false}
          />
        )}

        {!rightIcon1?.enabled && !rightIcon2?.enabled ? checkState() : null}

        {Platform.OS !== 'web' &&
          (finalStyle.borderWidth || 0) > 0 &&
          (finalStyle.borderStyle || 'none') !== 'none' && (
            <View
              style={{
                position: 'absolute',
                height: finalStyle.borderWidth || 0,
                bottom: 0,
                overflow: 'hidden',
                width,
              }}
            >
              <View
                style={{
                  ...Platform.select({
                    ios: {
                      borderRadius: 0,
                    },
                    android: {
                      borderRadius:
                        (finalStyle.borderStyle || 'solid') === 'solid'
                          ? 0
                          : 0.001,
                    },
                    default: {
                      borderRadius: 0,
                    },
                  }),
                  borderColor: borderColor,
                  borderStyle:
                    (finalStyle.borderStyle === 'none'
                      ? 'solid'
                      : finalStyle.borderStyle) || 'solid',
                  borderWidth: finalStyle.borderWidth || 0,
                }}
              />
            </View>
          )}
      </Appbar.Header>
    </View>
  );
};

export default AppBar;
